import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import log from 'loglevel';
import { useMergeLinkUi } from '../../../hooks/queries';
import STRING_KEYS from '../../../language/keys';
import {
  ACTION_SOURCE_LOCATION, ANALYTICS_EVENTS, EVENT_GROUP, PRODUCT_AREA,
} from '../../../consts/analytics';
import { PAGE_NAMES } from '../../../consts/pages';
import { useAnalyticsContext } from '../../../contexts/analytics/useAnalyticsContext';
import { IndeterminateLinearLoader } from '../../Common/Loaders/IndeterminateLinearLoader';
import Dialog from '../base-dialog/base-dialog.component';
import { ASSETS } from '../../../assets';
import { AlertSnackBar } from '../../Common/CustomSnackBar/AlertSnackBar';
import { DialogWidthSize } from '../base-dialog/base-dialog.enums';
import { AppRouting } from '../../../services/appRoutingResolver';
import { CONFIG } from '../../../config';
import { BaseSwitchSize } from '../../Common/base-switch/base-switch.enums';
import BaseSwitch from '../../Common/base-switch/base-switch.component';
import { THEME } from '../../../themes';
import {
  ArrowIcon,
  AtsComeetLogoImage,
  AtsGreenhouseLogoImage,
  AtsLogosImage,
  BodyContainer,
  Button,
  ButtonsContainer,
  ButtonTextAndImageWrapper,
  CheckedIcon,
  ChooseYourAtsText,
  Description,
  HeaderContainer,
  HeaderIcon,
  InitializeAtsInfo,
  LoaderWrapper,
  TextContainer,
  Title,
  TwoWayAtsContainer,
  TwoWayAtsInfoText,
  UnCheckedIcon,
} from './ats-initialization.styled';

const COMPONENT_ELEMENT_TYPE = {
  CLOSE: 'close',
  SYNC_TO_ATS_COMEET: 'sync_to_ats_comeet',
  SYNC_TO_ATS_GREENHOUSE: 'sync_to_ats_greenhouse',
  SYNC_TO_ATS_OTHER: 'sync_to_ats_other',
};

const eventPayloadAdditionalProperties = {
  [PRODUCT_AREA.HOMEPAGE]: {
    pageName: PAGE_NAMES.HOME_PAGE,
    actionSourceLocation: ACTION_SOURCE_LOCATION.POSITION_CARD,
  },
  [PRODUCT_AREA.MATCH_PAGE]: {
    pageName: PAGE_NAMES.MATCH_PAGE,
    actionSourceLocation: ACTION_SOURCE_LOCATION.ENGAGEMENT_MODAL,
  },
};

const logger = log.getLogger('ATS_INITIALIZATION_DIALOG');

export const AtsInitializationDialog = ({
  closeDialogHandler,
  productArea,
  isDialogOpen,
}: AtsInitializationProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const { analyticsTrackEvent } = useAnalyticsContext();
  const [isAlertSnackbarOpen, setIsAlertSnackbarOpen] = useState<boolean>(false);
  const [twoWaySyncApproved, setTwoWaySyncApproved] = useState<boolean>(true);
  const { open, isReady: isMergeReady, error: mergeError } = useMergeLinkUi(twoWaySyncApproved, isDialogOpen, closeDialogHandler);

  const sendClickEvent = (componentElementType: string) => {
    const payload = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_ATS_PROMO,
      eventGroup: EVENT_GROUP.CLICK,
      productArea,
      componentElementType,
      componentName: 'ats_modal_promo',
    };
    analyticsTrackEvent(payload);
  };

  useEffect(() => {
    if (mergeError) {
      logger.error('Error when tyring to open merge initialization modal', mergeError);
      setIsAlertSnackbarOpen(true);
    }
  }, [mergeError]);

  useEffect(() => {
    if (isDialogOpen) {
      const payload = {
        eventName: ANALYTICS_EVENTS.ATS_PROMO_MODAL_SHOWN,
        eventGroup: EVENT_GROUP.IMP,
        productArea,
        ...(eventPayloadAdditionalProperties[productArea] || {}),
      };
      analyticsTrackEvent(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  const onContinueWithOtherButtonClick = () => {
    open();
    closeDialogHandler();
    sendClickEvent(COMPONENT_ELEMENT_TYPE.SYNC_TO_ATS_OTHER);
  };

  const onContinueWithPredefinedAtsButtonClick = (componentElementType: string) => {
    sendClickEvent(componentElementType);
    AppRouting.openInNewTab(CONFIG.SCHEDULE_MEETING_URL_CS);
    closeDialogHandler();
  };

  const onCloseDialogHandler = () => {
    closeDialogHandler();
    sendClickEvent(COMPONENT_ELEMENT_TYPE.CLOSE);
  };

  return (
    <>
      <Dialog
        isOpen={ isDialogOpen }
        onClose={ onCloseDialogHandler }
        widthSize={ DialogWidthSize.Large }
      >
        <HeaderContainer>
          <HeaderIcon />
        </HeaderContainer>
        <BodyContainer>
          <>
            { !isMergeReady && (
              <LoaderWrapper>
                <IndeterminateLinearLoader loaderHeight="4px" />
              </LoaderWrapper>
            )}
          </>
          <InitializeAtsInfo>
            <TextContainer>
              <Title>{ translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.TITLE) }</Title>
              <Description>
                { translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.DESCRIPTION) }
              </Description>
              <ChooseYourAtsText>{ translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.CHOOSE_ATS) }</ChooseYourAtsText>
            </TextContainer>
            <ButtonsContainer>
              <Button onClick={ () => onContinueWithPredefinedAtsButtonClick(COMPONENT_ELEMENT_TYPE.SYNC_TO_ATS_GREENHOUSE) }>
                <ButtonTextAndImageWrapper>
                  { translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.CONTINUE_WITH) }
                  <AtsGreenhouseLogoImage src={ ASSETS.ATS_GREENHOUSE_LOGO } />
                </ButtonTextAndImageWrapper>
                <ArrowIcon />
              </Button>
              <Button onClick={ () => onContinueWithPredefinedAtsButtonClick(COMPONENT_ELEMENT_TYPE.SYNC_TO_ATS_COMEET) }>
                <ButtonTextAndImageWrapper>
                  { translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.CONTINUE_WITH) }
                  <AtsComeetLogoImage src={ ASSETS.ATS_COMEET_LOGO } />
                </ButtonTextAndImageWrapper>
                <ArrowIcon />
              </Button>
              <Button disabled={ !isMergeReady } onClick={ onContinueWithOtherButtonClick }>
                <ButtonTextAndImageWrapper>
                  { translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.CONTINUE_WITH) }
                  <AtsLogosImage src={ ASSETS.ATS_LOGOS } />
                </ButtonTextAndImageWrapper>
                <ArrowIcon />
              </Button>
            </ButtonsContainer>
            <TwoWayAtsContainer>
              <BaseSwitch
                checked={ twoWaySyncApproved }
                checkedColor={ THEME.COLORS.ACCENT }
                switchChangeHandler={ (value) => setTwoWaySyncApproved(value) }
                size={ BaseSwitchSize.Small }
                CheckedIcon={ <CheckedIcon /> }
                UnCheckedIcon={ <UnCheckedIcon /> }
              />
              <TwoWayAtsInfoText>
                <Trans i18nKey={ STRING_KEYS.ATS_INITIALIZATION_DIALOG.TWO_WAY_ATS_INFO_TEXT } />
              </TwoWayAtsInfoText>
            </TwoWayAtsContainer>
          </InitializeAtsInfo>
        </BodyContainer>
      </Dialog>
      <AlertSnackBar
        title={ translate(STRING_KEYS.ALERT) }
        description={ translate(STRING_KEYS.GLOBAL_ERROR_MESSAGE) }
        isOpen={ isAlertSnackbarOpen }
        onClose={ () => setIsAlertSnackbarOpen(false) }
        shouldAutoDismiss
      />
    </>
  );
};
